import React, { useState } from 'react';
import { authApi } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { FaLock, FaEye, FaEyeSlash, FaShieldAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function ForcePasswordChange() {
  const { user, school } = useAuth();
  const [form,    setForm]    = useState({ current_password: '', new_password: '', confirm: '' });
  const [show,    setShow]    = useState({ cur: false, new: false, con: false });
  const [saving,  setSaving]  = useState(false);

  const primary = school?.primary_color || '#0d6efd';

  const strength = (p) => {
    let s = 0;
    if (p.length >= 8)           s++;
    if (/[A-Z]/.test(p))         s++;
    if (/[0-9]/.test(p))         s++;
    if (/[^A-Za-z0-9]/.test(p))  s++;
    return s;
  };
  const str = strength(form.new_password);
  const strLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strColor = ['', '#ef4444', '#f59e0b', '#0d6efd', '#10b981'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error('Passwords do not match');
    if (form.new_password.length < 8)       return toast.error('Min 8 characters');
    if (form.new_password === form.current_password) return toast.error('New password must be different from current');
    setSaving(true);
    try {
      await authApi.changePassword({
        current_password: form.current_password,
        new_password:     form.new_password,
      });
      // Update user in localStorage to clear the flag
      const updated = { ...user, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(updated));
      window.dispatchEvent(new Event('storage'));
      toast.success('Password changed successfully! Welcome.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally { setSaving(false); }
  };

  const inp = (label, key, showKey) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#374151', marginBottom: 5 }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <FaLock style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
        <input
          className="sms-input"
          style={{ paddingLeft: 38, paddingRight: 38 }}
          type={show[showKey] ? 'text' : 'password'}
          value={form[key]}
          onChange={e => setForm({ ...form, [key]: e.target.value })}
          required
        />
        <button type="button" onClick={() => setShow(s => ({ ...s, [showKey]: !s[showKey] }))}
          style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
          {show[showKey] ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 36, width: '100%', maxWidth: 420, boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 60, height: 60, borderRadius: 16, background: `${primary}22`, border: `2px solid ${primary}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <FaShieldAlt size={26} color={primary} />
          </div>
          <h3 style={{ fontWeight: 800, color: '#111827', margin: 0 }}>Set Your Password</h3>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 6, lineHeight: 1.5 }}>
            Welcome, <strong>{user?.first_name}</strong>! For your security, please change your temporary password before continuing.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {inp('Current (Temporary) Password', 'current_password', 'cur')}
          {inp('New Password', 'new_password', 'new')}

          {/* Strength bar */}
          {form.new_password && (
            <div style={{ marginTop: -10, marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                {[1,2,3,4].map(i => (
                  <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= str ? strColor[str] : '#e5e7eb', transition: 'background 0.2s' }} />
                ))}
              </div>
              <div style={{ fontSize: '0.72rem', color: strColor[str], fontWeight: 600 }}>{strLabel[str]}</div>
            </div>
          )}

          {inp('Confirm New Password', 'confirm', 'con')}
          {form.confirm && form.new_password !== form.confirm && (
            <p style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: -8, marginBottom: 12 }}>Passwords do not match</p>
          )}

          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: '0.8rem', color: '#92400e' }}>
            💡 Use at least 8 characters with uppercase letters, numbers and symbols for a strong password.
          </div>

          <button type="submit" disabled={saving} style={{
            width: '100%', padding: '12px', borderRadius: 10, border: 'none',
            background: str >= 2 ? primary : '#e5e7eb',
            color: str >= 2 ? '#fff' : '#9ca3af',
            fontWeight: 700, fontSize: '0.95rem', cursor: str >= 2 ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s',
          }}>
            {saving ? 'Saving...' : 'Set Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
